<!-- Activities lead, tools follow the divider: the tools open a view rather than creating, so
     they hold a stable position as the activity set changes per tenant. -->
<ng-template #quickActionStrip>
  <ng-container *ngIf="quickActions?.length || hasQuickActionOverflow">
    <ion-button class="quick-action-tile" *ngFor="let action of quickActions; trackBy: trackById"
      [attr.data-testid]="'glance-quick-action-' + action.id"
      [class.quick-action-disabled]="action.isDisabled" [attr.aria-disabled]="action.isDisabled"
      [tooltip]="action.name" [display]="showTooltips" (events)="onTooltipVisibilityChange()" [attr.aria-label]="action.name"
      (click)="onQuickActionClick(action, $event)">
      <ion-icon [name]="action.icon" [src]="action.imgSrc"></ion-icon>
    </ion-button>
    <!-- Closes the activity group rather than opening the tools, so nothing interrupts
         "create an activity". -->
    <ion-button class="quick-action-tile" *ngIf="hasQuickActionOverflow" data-testid="glance-quick-action-more"
      [tooltip]="'MORE_ACTIVITY_TYPES' | translate" [display]="showTooltips" (events)="onTooltipVisibilityChange()" [attr.aria-label]="'MORE_ACTIVITY_TYPES' | translate"
      (click)="quickActionOverflow.emit($event)">
      <ion-icon src="assets/imgs/more-vertical.svg"></ion-icon>
    </ion-button>
    <span class="quick-action-divider" *ngIf="toolButtons.length"></span>
  </ng-container>
</ng-template>

<ng-container *ngIf="viewData">
  <ion-toolbar class="header-toolbar">
    <!-- One row of avatar, name block and icons, wrapping rather than branching on the device:
         the icons take the line below whenever the pane is too narrow to hold all three. -->
    <div class="header-toolbar-inner-wrapper-first"
      [ngClass]="(viewData.isExpanded || !viewData.enableExpand)?'expanded-view':'collapsed-view'">
      <div class="text-row">
        <ion-button class="glance-back" fill="clear" *ngIf="backButton" [attr.aria-label]="'BACK' | translate"
          (click)="back.emit(backButton.id)">
          <ion-icon [name]="backButton.icon"></ion-icon>
        </ion-button>
        <span class="avatar-box">
          <div class="avatar" *ngIf="viewData.avatarURL">
            <img class="avatar-img" src="{{viewData.avatarURL}}">
          </div>
          <div class="avatar userInitialsBox" *ngIf="!viewData.avatarURL">
            {{userInitials}}
          </div>
        </span>
        <div class="text-lines">
          <div class="first-line">
            <ion-text class="primary-glance-label">
              <ion-text class="primary-glance-label-text" (click)="showFullTextIfTruncated($event)"> {{viewData.name}} </ion-text>
            </ion-text>
            <ion-icon class="glance-name-action" *ngIf="nameAction" [src]="nameAction.imgSrc" role="button" tabindex="0"
              [class.disabled]="nameAction.isDisabled" [attr.aria-disabled]="!!nameAction.isDisabled" [attr.aria-label]="nameAction.name"
              [tooltip]="nameAction.name" [display]="showTooltips" (events)="onTooltipVisibilityChange()"
              (click)="onButtonClick(nameAction, $event)" (keydown.enter)="onButtonClick(nameAction, $event)"></ion-icon>
          </div>
          <span class="second-line" *ngIf="!viewData.hideLocation && (viewData.isExpanded || !viewData.enableExpand)">
            <ion-icon class="glance-line-icon" name="location-outline"></ion-icon>
            <ion-text class="glance-secondary-text" (click)="showFullTextIfTruncated($event)">
              <ion-text *ngIf="viewData.locationText; else nolocationtext">
                {{viewData.locationText}}
              </ion-text>
              <ng-template #nolocationtext>
                <ion-text class="placeholder-text">
                  {{'NO_LOCATION' | translate}}
                </ion-text>
              </ng-template>
            </ion-text>
          </span>
          <span class="second-line" *ngIf="viewData.lastInteraction">
            <ion-icon class="glance-line-icon" name="time-outline"></ion-icon>
            <ion-text class="glance-secondary-text last-interaction-text" (click)="showFullTextIfTruncated($event)">
              {{'GLANCE_LAST_INTERACTION' | translate}}
              <span class="last-interaction-type" *ngIf="viewData.lastInteraction.label">{{viewData.lastInteraction.label}}</span>
              <ng-container *ngIf="viewData.lastInteraction.detail"> · {{viewData.lastInteraction.detail}}</ng-container>
            </ion-text>
          </span>
        </div>
        <ion-buttons class="end-slot"
          *ngIf="viewData.enableExpand || toolButtons.length > 0 || quickActions?.length || hasQuickActionOverflow">
          <!-- Collapsed too: the strip is what the card is for, and the chevron hides the
               fields, not the activities. -->
          <ng-container [ngTemplateOutlet]="quickActionStrip"></ng-container>
          <ion-button class="quick-action-tile" *ngFor="let button of toolButtons; trackBy: trackById"
            [disabled]="button.isDisabled" [tooltip]="button.name" [display]="showTooltips" (events)="onTooltipVisibilityChange()" [attr.aria-label]="button.name"
            (click)="onButtonClick(button,$event)">
            <ion-icon [name]="button.icon" [src]="button.imgSrc"></ion-icon>
          </ion-button>
          <ng-container *ngIf="viewData.enableExpand">
            <ion-button (click)="onExpandClick()" *ngIf="!viewData.isExpanded">
              <ion-icon name="chevron-down-outline"></ion-icon>
            </ion-button>
            <ion-button (click)="onExpandClick()" *ngIf="viewData.isExpanded">
              <ion-icon name="chevron-up-outline"></ion-icon>
            </ion-button>
          </ng-container>
        </ion-buttons>
      </div>
    </div>

    <!-- Columns come from the width, not from a breakpoint: the grid fits as many 132px
         tracks as there is room for, so a phone shows two and a wide pane four. -->
    <div class="header-toolbar-inner-wrapper-second" *ngIf="viewData.isExpanded || !viewData.enableExpand"
      [ngClass]="(viewData.isExpanded || !viewData.enableExpand)?'expanded-view':'collapsed-view'">
      <div class="glance-field-grid" *ngIf="fields.length">
        <div class="glance-field" *ngFor="let field of fields; trackBy: trackByLabel">
          <ion-text class="label-text" *ngIf="field.label" (click)="showFullTextIfTruncated($event)">{{field.label}}</ion-text>
          <ion-text class="value-text" *ngIf="field.value" (click)="showFullTextIfTruncated($event)">{{field.value}}</ion-text>
        </div>
      </div>
      <!-- It edits this row, so it sits at the end of it rather than among the tools. -->
      <ion-button class="glance-personalize" fill="clear" *ngIf="personalizeButton"
        [disabled]="personalizeButton.isDisabled" [tooltip]="personalizeButton.name" [display]="showTooltips" (events)="onTooltipVisibilityChange()"
        [attr.aria-label]="personalizeButton.name" (click)="onButtonClick(personalizeButton, $event)">
        <ion-icon [name]="personalizeButton.icon" [src]="personalizeButton.imgSrc"></ion-icon>
      </ion-button>
    </div>
  </ion-toolbar>
</ng-container>
